/**
 * API service for On-Call Manager
 * All endpoints under /api/oncall/*
 */

import apiClient from '@/api/client';
import type {
  AvailableMember,
  CurrentOnCallResponse,
  OnCallTicketCheck,
  TicketEvaluationResult,
  OnCallSmsHistoryItem,
  OnCallSmsMessageSummary,
  SuccessResponse,
  TestNotificationResponse,
  SmsEnrollment,
  SmsEnrollmentByUser,
  EnrollPhoneRequest,
  EnrollPhoneResponse
} from './types';

/**
 * Get the current on-call member (returns just the member ID string as plain text)
 */
export const getCurrentOnCallMember = async (): Promise<CurrentOnCallResponse> => {
  const response = await apiClient.get('/api/oncall/current', {
    responseType: 'text'
  });
  // Response is plain text, not JSON
  const text = response.data as string;
  return text === '' ? null : text;
};

/**
 * Set the current on-call member (send just the member ID string)
 */
export const setCurrentOnCallMember = async (memberId: string): Promise<SuccessResponse> => {
  const response = await apiClient.post<SuccessResponse>('/api/oncall/set', JSON.stringify(memberId), {
    headers: { 'Content-Type': 'application/json' }
  });
  return response.data;
};

/**
 * Get all available members from ConnectWise
 */
export const getAvailableMembers = async (): Promise<AvailableMember[]> => {
  const response = await apiClient.get<AvailableMember[]>('/api/oncall/available');
  return response.data;
};

/**
 * Get the configured on-call members list
 */
export const getOnCallMembers = async (): Promise<string[]> => {
  const response = await apiClient.get<string[]>('/api/oncall/members');
  return response.data;
};

/**
 * Update the on-call members list
 */
export const setOnCallMembers = async (memberIds: string[]): Promise<SuccessResponse> => {
  const response = await apiClient.post<SuccessResponse>('/api/oncall/set-members', memberIds);
  return response.data;
};

/**
 * Get the always-send notification recipients list
 */
export const getAlwaysSendList = async (): Promise<string[]> => {
  const response = await apiClient.get<string[]>('/api/oncall/always-send');
  return response.data;
};

/**
 * Update the always-send notification recipients list
 */
export const setAlwaysSendList = async (memberIds: string[]): Promise<SuccessResponse> => {
  const response = await apiClient.post<SuccessResponse>('/api/oncall/set-always-send', memberIds);
  return response.data;
};

/**
 * Get the notification repeat interval (minutes) - returns plain text
 */
export const getNotificationRepeatMinutes = async (): Promise<number | null> => {
  const response = await apiClient.get('/api/oncall/notification-repeat-minutes', {
    responseType: 'text'
  });
  // Response is plain text, not JSON
  const text = response.data as string;
  return text === '' || text === null ? null : parseInt(text, 10);
};

/**
 * Set the notification repeat interval (minutes)
 */
export const setNotificationRepeatMinutes = async (minutes: number | null): Promise<SuccessResponse> => {
  const response = await apiClient.post<SuccessResponse>('/api/oncall/set-notification-repeat-minutes', minutes);
  return response.data;
};

/**
 * Get the initial notification delay interval (minutes) - returns plain text
 */
export const getNotificationInitialDelayMinutes = async (): Promise<number | null> => {
  const response = await apiClient.get('/api/oncall/notification-initial-delay-minutes', {
    responseType: 'text'
  });
  const text = response.data as string;
  return text === '' || text === null ? null : parseInt(text, 10);
};

/**
 * Set the initial notification delay interval (minutes)
 */
export const setNotificationInitialDelayMinutes = async (minutes: number | null): Promise<SuccessResponse> => {
  const response = await apiClient.post<SuccessResponse>('/api/oncall/set-notification-initial-delay-minutes', minutes);
  return response.data;
};

/**
 * Send a test notification to the current on-call member
 */
export const sendTestNotification = async (): Promise<TestNotificationResponse> => {
  const response = await apiClient.post<TestNotificationResponse>('/api/oncall/test-notification');
  return response.data;
};

/**
 * Get recent notification history (last 20 ticket checks)
 */
export const getRecentNotifications = async (): Promise<OnCallTicketCheck[]> => {
  const response = await apiClient.get<OnCallTicketCheck[]>('/api/oncall/recent-notifications');
  return response.data;
};

/**
 * Get recent outbound SMS history.
 */
export const getSmsHistory = async (): Promise<OnCallSmsHistoryItem[]> => {
  const response = await apiClient.get<OnCallSmsHistoryItem[]>('/api/oncall/sms-history');
  return response.data;
};

/**
 * Get SMS messages associated with a specific ticket check row.
 */
export const getSmsForTicketCheck = async (ticketCheckId: number): Promise<OnCallSmsMessageSummary[]> => {
  const response = await apiClient.get<OnCallSmsMessageSummary[]>(`/api/oncall/recent-notifications/${ticketCheckId}/sms`);
  return response.data;
};

/**
 * Evaluate a ticket against notification criteria (developer tool)
 */
export const evaluateTicket = async (ticketId: number): Promise<TicketEvaluationResult> => {
  const response = await apiClient.post<TicketEvaluationResult>(`/api/oncall/evaluate-ticket/${ticketId}`);
  return response.data;
};

/**
 * Get SMS enrollments for the current user
 */
export const getSmsEnrollments = async (): Promise<SmsEnrollment[]> => {
  const response = await apiClient.get<SmsEnrollment[]>('/api/oncallsms/enrollments');
  return response.data || [];
};

/**
 * Get SMS enrollments for all users (Developer and Service Delivery Manager only)
 */
export const getAllSmsEnrollments = async (): Promise<SmsEnrollmentByUser[]> => {
  const response = await apiClient.get<SmsEnrollmentByUser[]>('/api/oncallsms/enrollments/all');
  return response.data || [];
};

/**
 * Get the configured SMS sender phone number
 */
export const getSmsFromNumber = async (): Promise<string> => {
  const response = await apiClient.get<{ fromNumber: string }>('/api/oncallsms/from-number');
  return response.data.fromNumber;
};

/**
 * Enroll a phone number for SMS notifications
 */
export const enrollPhoneNumber = async (phoneNumber: string): Promise<EnrollPhoneResponse> => {
  const request: EnrollPhoneRequest = { phoneNumber };
  const response = await apiClient.post<EnrollPhoneResponse>('/api/oncallsms/enroll', request);
  return response.data;
};

/**
 * Remove a phone number from SMS notifications
 */
export const removePhoneNumber = async (enrollmentId: number): Promise<SuccessResponse> => {
  const response = await apiClient.delete<SuccessResponse>(`/api/oncallsms/enrollments/${enrollmentId}`);
  return response.data;
};

/**
 * Send a test SMS to verify enrollment
 */
export const sendTestSms = async (enrollmentId: number): Promise<SuccessResponse> => {
  const response = await apiClient.post<SuccessResponse>(`/api/oncallsms/enrollments/${enrollmentId}/test`);
  return response.data;
};
